import { AlertTriangle, CheckCircle2, RotateCcw, X, XCircle } from "lucide-react";
import React from "react";
import AppButton from "../../../../components/ui/AppButton";
import Table, { Column } from "../../../../components/ui/dashboard/Table";
import {
  DispatchAlreadyImported,
  DispatchImportedJourney,
  DispatchImportResult,
} from "../../@types";
import {
  formatImportAmount,
  formatImportDate,
  ImportIssuesTable,
  ImportSection,
  ImportStatsGrid,
} from "./ImportShared";

interface ImportResultPanelProps {
  result: DispatchImportResult;
  fileName?: string;
  onReverse?: () => void;
  onDismiss?: () => void;
  // Rendered inside a modal: drop the card chrome.
  embedded?: boolean;
}

const importedColumns: Column<DispatchImportedJourney>[] = [
  {
    key: "dispatchNumber",
    header: "Trip number",
    render: (value: string) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "tripDate",
    header: "Trip date",
    render: (value: string) => (
      <span className="text-hColor">{formatImportDate(value)}</span>
    ),
  },
  {
    key: "licensePlate",
    header: "Vehicle",
    render: (value: string) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "driverName",
    header: "Driver",
    render: (value: string) => (
      <span className="text-hColor">{value || "-"}</span>
    ),
  },
  {
    key: "origin",
    header: "Route",
    render: (_: string, item: DispatchImportedJourney) => (
      <span className="text-hColor">
        {item.origin} → {item.destination}
      </span>
    ),
  },
  {
    key: "legs",
    header: "Legs",
    render: (value: number) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "passengers",
    header: "Passengers",
    render: (value: number) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "grossIncome",
    header: "Gross income (NGN)",
    render: (value: number) => (
      <span className="text-green-600">{formatImportAmount(value)}</span>
    ),
  },
  {
    key: "netBanked",
    header: "Net banked (NGN)",
    render: (value: number) => (
      <span className="text-green-700">{formatImportAmount(value)}</span>
    ),
  },
];

const alreadyImportedColumns: Column<DispatchAlreadyImported>[] = [
  {
    key: "loadingNumber",
    header: "Loading number",
    render: (value: string) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "sourceRow",
    header: "Row",
    render: (value: number) => <span className="text-hColor">{value}</span>,
  },
  {
    key: "tripDate",
    header: "Trip date",
    render: (value: string) => (
      <span className="text-hColor">{formatImportDate(value)}</span>
    ),
  },
  {
    key: "licensePlate",
    header: "Vehicle",
    render: (value: string) => <span className="text-hColor">{value}</span>,
  },
];

const ImportResultPanel: React.FC<ImportResultPanelProps> = ({
  result,
  fileName,
  onReverse,
  onDismiss,
  embedded = false,
}) => {
  const errors = result.errors || [];
  const warnings = result.warnings || [];
  const imported = result.imported || [];
  const alreadyImported = result.alreadyImported || [];
  const missingColumns = result.missingColumns || [];

  const failed =
    result.journeysImported === 0 &&
    (errors.length > 0 || missingColumns.length > 0 || result.journeysFailed > 0);
  const hasIssues = failed || errors.length > 0 || result.journeysFailed > 0;

  const headline = result.validateOnly
    ? "Validation complete — nothing was saved"
    : failed
    ? "Import failed"
    : hasIssues || warnings.length > 0
    ? "Import completed with issues"
    : "Import successful";

  const HeadlineIcon = failed
    ? XCircle
    : hasIssues || warnings.length > 0
    ? AlertTriangle
    : CheckCircle2;
  const headlineColor = failed
    ? "text-red-600"
    : hasIssues || warnings.length > 0
    ? "text-yellow-600"
    : "text-green-600";

  const canReverse =
    !!onReverse &&
    !result.validateOnly &&
    !!result.importBatchId &&
    result.journeysImported > 0;

  return (
    <div
      className={`space-y-6 ${embedded ? "" : "bg-white rounded-[10px] p-4"}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <HeadlineIcon className={`w-6 h-6 shrink-0 ${headlineColor}`} />
          <div>
            <h2 className="text-[16px] font-semibold text-hColor">
              {headline}
            </h2>
            <p className="text-[13px] text-pColor">
              {[fileName, result.sheetName && `Sheet: ${result.sheetName}`]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canReverse && (
            <AppButton
              variant="danger"
              appearance="outline"
              size="sm"
              icon={<RotateCcw className="w-4 h-4" />}
              onClick={onReverse}
            >
              Reverse import
            </AppButton>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Dismiss import result"
            >
              <X className="w-4 h-4 text-hColor" />
            </button>
          )}
        </div>
      </div>

      <ImportStatsGrid
        stats={[
          { label: "Rows read", value: result.rowsRead },
          { label: "Rows accepted", value: result.rowsAccepted },
          { label: "Journeys found", value: result.journeysFound },
          {
            label: "Journeys imported",
            value: result.journeysImported,
            tone: "success",
          },
          {
            label: "Journeys skipped",
            value: result.journeysSkipped,
            tone: result.journeysSkipped ? "warning" : "default",
          },
          {
            label: "Journeys failed",
            value: result.journeysFailed,
            tone: result.journeysFailed ? "danger" : "default",
          },
          {
            label: "Errors",
            value: errors.length,
            tone: errors.length ? "danger" : "default",
          },
          {
            label: "Warnings",
            value: warnings.length,
            tone: warnings.length ? "warning" : "default",
          },
        ]}
      />

      {missingColumns.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-2">
          <p className="text-[14px] font-semibold text-red-700">
            Missing required columns
          </p>
          <div className="flex flex-wrap gap-2">
            {missingColumns.map((column) => (
              <span
                key={column}
                className="text-[12px] text-red-700 bg-white border border-red-200 rounded-full px-2.5 py-0.5"
              >
                {column}
              </span>
            ))}
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <ImportSection title="Errors" count={errors.length}>
          <ImportIssuesTable issues={errors} tone="danger" />
        </ImportSection>
      )}

      {warnings.length > 0 && (
        <ImportSection title="Warnings" count={warnings.length}>
          <ImportIssuesTable issues={warnings} tone="warning" />
        </ImportSection>
      )}

      {imported.length > 0 && (
        <ImportSection
          title={result.validateOnly ? "Journeys to import" : "Imported journeys"}
          count={imported.length}
        >
          <Table data={imported} columns={importedColumns} />
        </ImportSection>
      )}

      {alreadyImported.length > 0 && (
        <ImportSection
          title="Already imported (skipped)"
          count={alreadyImported.length}
        >
          <Table data={alreadyImported} columns={alreadyImportedColumns} />
        </ImportSection>
      )}
    </div>
  );
};

export default ImportResultPanel;
